import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { clearStaleAuthState } from "@/utils/sessionCacheService";

const AUTH_TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const AUTH_URL = "https://front.microcentrum.cl/v1/auth?op=refreshToken";

/** Segundos antes del vencimiento para renovar proactivamente. */
const REFRESH_SKEW_SECONDS = 5 * 60;
/** Si quedan menos de esto, renovar de inmediato en ensureValidSession. */
const HARD_REFRESH_SECONDS = 2 * 60;

class AuthService {
  constructor() {
    this.token = localStorage.getItem(AUTH_TOKEN_KEY);
    this.lastRoute = localStorage.getItem("lastRoute");
    this.refreshTokenTimeoutId = null;
    this.refreshInFlight = null;

    // Reprogramar timer si ya hay sesión al cargar el bundle.
    if (this.getToken() || this.getRefreshToken()) {
      this.setupTokenRefresh();
    }
  }

  getToken() {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Guarda idToken y, si viene, refreshToken (login / OAuth / magic).
   * Cognito no siempre devuelve un refresh nuevo al renovar; no borrar el existente.
   */
  setSession({ idToken, refreshToken } = {}) {
    if (idToken) {
      localStorage.setItem(AUTH_TOKEN_KEY, idToken);
      this.token = idToken;
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    this.setupTokenRefresh();
  }

  /** Compat: solo idToken (flujos antiguos). Preferir setSession. */
  setToken(token, refreshToken) {
    this.setSession({ idToken: token, refreshToken });
  }

  clearSessionTokens() {
    if (this.refreshTokenTimeoutId) {
      clearTimeout(this.refreshTokenTimeoutId);
      this.refreshTokenTimeoutId = null;
    }
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    this.token = null;
    clearStaleAuthState();
  }

  /**
   * Renueva el idToken usando el refreshToken persistido.
   * @returns {Promise<boolean>}
   */
  async refreshSession() {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = (async () => {
      const storedRefresh = this.getRefreshToken();
      if (!storedRefresh) {
        console.warn("[authService] Sin refreshToken; no se puede renovar sesión");
        return false;
      }

      try {
        const response = await axios.post(
          AUTH_URL,
          { refreshToken: storedRefresh },
          { headers: { "Content-Type": "application/json" } },
        );

        const idToken = response?.data?.idToken;
        if (!idToken) {
          console.error("[authService] Refresh sin idToken en respuesta");
          return false;
        }

        // Cognito suele no devolver refresh nuevo; conservar el actual.
        const nextRefresh =
          response.data.refreshToken ||
          response.data.refresh_token ||
          storedRefresh;

        this.setSession({ idToken, refreshToken: nextRefresh });
        return true;
      } catch (error) {
        const status = error?.response?.status;
        console.error("[authService] Error refreshing token:", status, error);
        if (status === 401 || status === 400) {
          this.clearSessionTokens();
        }
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  /** Alias histórico (antes mal cableado). */
  async refreshToken() {
    return this.refreshSession();
  }

  setupTokenRefresh() {
    if (this.refreshTokenTimeoutId) {
      clearTimeout(this.refreshTokenTimeoutId);
      this.refreshTokenTimeoutId = null;
    }

    const token = this.getToken();
    if (!token) {
      // Solo refresh: intentar renovar pronto para obtener idToken.
      if (this.getRefreshToken()) {
        this.refreshTokenTimeoutId = setTimeout(() => {
          void this.refreshSession();
        }, 1000);
      }
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const expirationMs = decodedToken.exp * 1000;
      const refreshAt = expirationMs - REFRESH_SKEW_SECONDS * 1000;
      const delay = Math.max(5_000, refreshAt - Date.now());

      this.refreshTokenTimeoutId = setTimeout(() => {
        void this.refreshSession();
      }, delay);
    } catch (error) {
      console.error("[authService] Error setting up token refresh:", error);
    }
  }

  /** idToken aún válido (sin mirar refresh). */
  isIdTokenFresh(minSecondsLeft = 0) {
    const token = this.getToken();
    if (!token) return false;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken.exp - Date.now() / 1000 > minSecondsLeft;
    } catch {
      return false;
    }
  }

  /**
   * Sesión usable: idToken vigente o refresh disponible para renovar.
   * Sync (UI / redirect). La renovación real va en ensureValidSession.
   */
  isAuthenticated() {
    if (this.isIdTokenFresh(0)) return true;
    return Boolean(this.getRefreshToken());
  }

  /**
   * Garantiza un idToken usable. Usar en guards / resume.
   */
  async ensureValidSession() {
    if (this.isIdTokenFresh(HARD_REFRESH_SECONDS)) {
      if (!this.isIdTokenFresh(REFRESH_SKEW_SECONDS)) {
        void this.refreshSession();
      }
      return true;
    }

    if (!this.getRefreshToken()) {
      return false;
    }

    return this.refreshSession();
  }

  saveLastRoute(route) {
    if (route.name !== "login") {
      localStorage.setItem("lastRoute", route.fullPath);
      this.lastRoute = route.fullPath;
    }
  }

  getLastRoute() {
    return localStorage.getItem("lastRoute") || "/";
  }

  logout() {
    this.clearSessionTokens();
    localStorage.removeItem("lastRoute");
    this.lastRoute = null;
  }
}

export const authService = new AuthService();
